import { formatCurrency } from "@sa-nu/shared-utils/price";
import clsx from "clsx";

import type { FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import type {
  CancelFeePolicy,
  OwnerPlanType,
  StayTicketType,
} from "../../../__generated__/gql/graphql";
import { DxIcon } from "../dx-icons";

import { splitCancelFeeRule } from "./_utils/split-cancel-fee-rule";

export const StayTicketUnitForCancelFeePolicyFragment = graphql(`
  fragment StayTicketUnitForCancelFeePolicyFragment on StayTicketUnit {
    reservationInfo {
      type
      ownerPlanType
      cancelFeePolicy {
        __typename
        ... on CancelFeeRatePolicy {
          immediate
          before1DaysRate
          before3DaysRate
          before7DaysRate
        }
        ... on CancelFeeFixedPolicy {
          immediate
          before1DaysFee
          before3DaysFee
          before7DaysFee
        }
      }
    }
  }
`);

/**
 * キャンセル料金のルールを表示するコンポーネント
 *
 * cancelFeePolicyData が渡された時は、StayTicket として扱う。
 * 渡されない（`undefined`の）場合は、OTA として扱う。
 *
 * よって、上記以外のプロバイダがの場合には、このコンポーネントを呼び出さないようにする。
 * （暫定対応）
 */
export type CancelFeeOTAMessages = {
  title: string;
  intro: string;
  rule7days: string;
  rule3days: string;
  noShow: string;
};

const JA_MESSAGES_OTA: CancelFeeOTAMessages = {
  title: "キャンセル料金のルールについて",
  intro:
    "キャンセルや予約変更により宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。",
  rule7days: "7日前15時以降：宿泊料金の60%",
  rule3days: "3日前15時以降：宿泊料金の100%",
  noShow:
    "宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり宿泊料金の100%が請求されます。",
};

/**
 * rate 型ポリシーの料率を何に掛けるか。`baseFee` はオーナープランの1泊あたり基本料金。
 * `additionalFee` は FullOwner ギフトでゲストが払う追加料金（Owners 規約第8条）。
 */
export type CancelFeeRateBasis =
  | "accommodationFee"
  | "baseFee"
  | "additionalFee";

export type CancelFeeStayTicketMessages = {
  title: string;
  intro: string;
  introImmediate: string;
  cannotChangeReservation: string;
  cannotChangeReservationImmediate: string;
  ruleByRate: (params: {
    daysBeforeCheckin: 7 | 3 | 1;
    rate: number;
    rateBasis: CancelFeeRateBasis;
  }) => string;
  ruleByFee: (params: { daysBeforeCheckin: 7 | 3 | 1; fee: number }) => string;
  ruleImmediate: () => string;
  noShowByRate: (params: {
    rate: number;
    rateBasis: CancelFeeRateBasis;
  }) => string;
  /** immediate では before1Days* が 0 に正規化されるため、ポリシー値から組み立てず固定文言にする */
  noShowImmediate: () => string;
  noShowByFee: (params: { fee: number }) => string;
};

const formatPeriodJa = (daysBeforeCheckin: 7 | 3 | 1): string =>
  daysBeforeCheckin === 1 ? "前日15時以降" : `${daysBeforeCheckin}日前15時以降`;

const RATE_BASIS_JA = {
  accommodationFee: "宿泊料金",
  baseFee: "基本料金",
  additionalFee: "追加料金",
} as const satisfies Record<CancelFeeRateBasis, string>;

export const formatRateBasisJa = (rateBasis: CancelFeeRateBasis): string =>
  RATE_BASIS_JA[rateBasis];

// emailapp の getRateFeeLabel / core-system の buildCancellationPolicyContent と判定を揃える
const resolveRateBasis = (reservationInfo: {
  type: StayTicketType;
  ownerPlanType?: OwnerPlanType | null;
}): CancelFeeRateBasis => {
  if (reservationInfo.type !== "StayGiftFromOwners") {
    return "accommodationFee";
  }
  return reservationInfo.ownerPlanType === "CoOwners"
    ? "baseFee"
    : "additionalFee";
};

const JA_MESSAGES_STAY_TICKET: CancelFeeStayTicketMessages = {
  title: "キャンセル料金のルールについて",
  intro:
    "キャンセルや予約変更により宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。",
  introImmediate:
    "キャンセルにより宿泊をとりやめた場合、宿泊日ごとに以下の料金が発生します。",
  cannotChangeReservation:
    "チェックイン日の前日15時以降は、チェックイン日と宿泊施設を変更できません。",
  cannotChangeReservationImmediate: "予約内容の変更はできません。",
  ruleByRate: ({ daysBeforeCheckin, rate, rateBasis }) =>
    `${formatPeriodJa(daysBeforeCheckin)}：${formatRateBasisJa(rateBasis)}の${rate}%`,
  ruleByFee: ({ daysBeforeCheckin, fee }) =>
    `${formatPeriodJa(daysBeforeCheckin)}：${formatCurrency(fee, {
      unitCharacter: "円",
    })}`,
  ruleImmediate: () => "予約確定後：宿泊料金の100%",
  noShowByRate: ({ rate, rateBasis }) =>
    `宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり${formatRateBasisJa(rateBasis)}の${rate}%が請求されます。`,
  noShowImmediate: () =>
    "宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり宿泊料金の100%が請求されます。",
  noShowByFee: ({ fee }) =>
    `宿泊日の翌日11時までにチェックインがない場合、ノーショー（No Show）料金として、1泊あたり${formatCurrency(
      fee,
      { unitCharacter: "円" },
    )}が請求されます。`,
};

export function CancelFeePolicyArea({
  stayTicketUnit,
  noShowNoticeHidden = false,
  cannotChangeReservationNoticeHidden = false,
  otaMessages,
  stayTicketMessages,
}: {
  stayTicketUnit?: FragmentType<
    typeof StayTicketUnitForCancelFeePolicyFragment
  > | null;
  noShowNoticeHidden?: boolean;
  cannotChangeReservationNoticeHidden?: boolean;
  otaMessages?: CancelFeeOTAMessages;
  stayTicketMessages?: CancelFeeStayTicketMessages;
}) {
  const stayTicketData = useFragment(
    StayTicketUnitForCancelFeePolicyFragment,
    stayTicketUnit,
  );

  if (stayTicketData) {
    const rateBasis = resolveRateBasis(stayTicketData.reservationInfo);
    return (
      <CancelFeePolicyAreaForStayTicket
        cancelFeePolicy={stayTicketData.reservationInfo.cancelFeePolicy}
        noShowNoticeHidden={noShowNoticeHidden}
        cannotChangeReservationNoticeHidden={
          cannotChangeReservationNoticeHidden
        }
        rateBasis={rateBasis}
        messages={stayTicketMessages}
      />
    );
  } else {
    return <CancelFeePolicyAreaForOTA messages={otaMessages} />;
  }
}

function CancelFeePolicyAreaForStayTicket({
  cancelFeePolicy,
  noShowNoticeHidden = false,
  cannotChangeReservationNoticeHidden = false,
  rateBasis,
  messages = JA_MESSAGES_STAY_TICKET,
}: {
  cancelFeePolicy: CancelFeePolicy;
  noShowNoticeHidden?: boolean;
  cannotChangeReservationNoticeHidden?: boolean;
  rateBasis: CancelFeeRateBasis;
  messages?: CancelFeeStayTicketMessages;
}) {
  if (!cancelFeePolicy) return null;

  // immediate では before*Days が 0 に正規化される（既存データには旧値が残る）ため、
  // 段階表示のフィルタに乗せると「0%」「0円」や旧値の誤表示になる
  const rules = cancelFeePolicy.immediate
    ? [messages.ruleImmediate()]
    : buildDaysBasedRules(cancelFeePolicy, messages, rateBasis);

  const noShowFee = cancelFeePolicy.immediate
    ? messages.noShowImmediate()
    : cancelFeePolicy.__typename === "CancelFeeRatePolicy"
      ? messages.noShowByRate({
          rate: cancelFeePolicy.before1DaysRate * 100,
          rateBasis,
        })
      : messages.noShowByFee({ fee: cancelFeePolicy.before1DaysFee });

  return (
    <CancelFeePolicyTable
      immediate={cancelFeePolicy.immediate}
      title={messages.title}
      intro={
        cancelFeePolicy.immediate ? messages.introImmediate : messages.intro
      }
      rules={rules}
      notices={[
        ...(noShowNoticeHidden ? [] : [noShowFee]),
        ...(cannotChangeReservationNoticeHidden
          ? []
          : [
              cancelFeePolicy.immediate
                ? messages.cannotChangeReservationImmediate
                : messages.cannotChangeReservation,
            ]),
      ]}
    />
  );
}

function CancelFeePolicyTable({
  immediate,
  title,
  intro,
  rules,
  notices,
}: {
  immediate: boolean;
  title: string;
  intro: string;
  rules: string[];
  notices: string[];
}) {
  return (
    <div
      className={clsx(
        "font-notojp text-dx-text-default flex flex-col gap-[12px] p-[24px]",
        immediate
          ? "border-dx-text-error bg-dx-base-white rounded-[6px] border-2"
          : "bg-slightbeige-100",
      )}
    >
      <div
        className={clsx(
          "flex",
          immediate
            ? "text-dx-text-error items-center gap-[8px]"
            : "items-start gap-[12px]",
        )}
      >
        <DxIcon src="/i/dx/warn.svg" size={22} />
        <h3 className="text-[16px] leading-[24px] font-bold">{title}</h3>
      </div>
      {rules.length > 0 && (
        <>
          <p className="text-[14px] leading-[24px] font-bold">{intro}</p>
          <div
            className={clsx(
              "overflow-hidden rounded-[4px] px-[16px] py-[2px]",
              immediate
                ? "bg-dx-bg-accent leading-[24px]"
                : "bg-dx-base-white text-skogecha-100 leading-[22px]",
            )}
          >
            <table className="w-full text-[14px]">
              <tbody>
                {rules.map(splitCancelFeeRule).map(({ period, value }) => (
                  <tr
                    key={period}
                    className="border-dx-border-weak border-t first:border-t-0"
                  >
                    <th
                      scope="row"
                      className={clsx(
                        "text-left",
                        immediate
                          ? "py-[13px] font-bold"
                          : "py-[11px] font-normal",
                      )}
                    >
                      {period}
                    </th>
                    <td
                      className={clsx(
                        "text-right font-bold",
                        immediate
                          ? "text-dx-text-error py-[13px] text-[15px]"
                          : "py-[11px]",
                      )}
                    >
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {notices.length > 0 && (
        <div className="flex flex-col text-[14px] leading-[24px]">
          {notices.map((notice) => (
            <p key={notice}>{notice}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// 同一金額／同一レートの時、表記を省略する
function buildDaysBasedRules(
  cancelFeePolicy: CancelFeePolicy,
  messages: CancelFeeStayTicketMessages,
  rateBasis: CancelFeeRateBasis,
): string[] {
  if (cancelFeePolicy.__typename === "CancelFeeRatePolicy") {
    const rateRules: { daysBeforeCheckin: 7 | 3 | 1; rate: number }[] = [];
    const { before7DaysRate, before3DaysRate, before1DaysRate } =
      cancelFeePolicy;

    // 7日前のルール
    if (before7DaysRate > 0.0) {
      rateRules.push({ daysBeforeCheckin: 7, rate: before7DaysRate });
    }

    // 3日前のルール（7日前と同じレートでない場合のみ追加）
    if (before3DaysRate > 0.0 && before3DaysRate !== before7DaysRate) {
      rateRules.push({ daysBeforeCheckin: 3, rate: before3DaysRate });
    }

    // 前日のルール（3日前と同じレートでない場合のみ追加）
    if (before1DaysRate > 0.0 && before1DaysRate !== before3DaysRate) {
      rateRules.push({ daysBeforeCheckin: 1, rate: before1DaysRate });
    }

    return rateRules.map((rule) =>
      messages.ruleByRate({
        daysBeforeCheckin: rule.daysBeforeCheckin,
        rate: rule.rate * 100,
        rateBasis,
      }),
    );
  } else {
    const feeRules: { daysBeforeCheckin: 7 | 3 | 1; fee: number }[] = [];
    const { before7DaysFee, before3DaysFee, before1DaysFee } = cancelFeePolicy;

    // 7日前のルール
    if (before7DaysFee > 0) {
      feeRules.push({ daysBeforeCheckin: 7, fee: before7DaysFee });
    }

    // 3日前のルール（7日前と同じ金額でない場合のみ追加）
    if (before3DaysFee > 0 && before3DaysFee !== before7DaysFee) {
      feeRules.push({ daysBeforeCheckin: 3, fee: before3DaysFee });
    }

    // 前日のルール（3日前と同じ金額でない場合のみ追加）
    if (before1DaysFee > 0 && before1DaysFee !== before3DaysFee) {
      feeRules.push({ daysBeforeCheckin: 1, fee: before1DaysFee });
    }

    return feeRules.map((rule) =>
      messages.ruleByFee({
        daysBeforeCheckin: rule.daysBeforeCheckin,
        fee: rule.fee,
      }),
    );
  }
}

// TODO: キャンセル料金のルールを API から取得できるようにする。
function CancelFeePolicyAreaForOTA({
  messages = JA_MESSAGES_OTA,
}: {
  messages?: CancelFeeOTAMessages;
}) {
  return (
    <CancelFeePolicyTable
      immediate={false}
      title={messages.title}
      intro={messages.intro}
      rules={[messages.rule7days, messages.rule3days]}
      notices={[messages.noShow]}
    />
  );
}
